import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const enc = new TextEncoder();

const leadingZeroBits = (bytes) => {
  let bits = 0;
  for (const b of bytes) {
    if (b === 0) { bits += 8; continue; }
    bits += Math.clz32(b) - 24;
    break;
  }
  return bits;
};

async function solve(nonce, difficulty) {
  let i = 0;
  for (;;) {
    const batch = [];
    for (let k = 0; k < 64; k++) batch.push(crypto.subtle.digest("SHA-256", enc.encode(`${nonce}:${i + k}`)));
    const digests = await Promise.all(batch);
    for (let k = 0; k < 64; k++) {
      if (leadingZeroBits(new Uint8Array(digests[k])) >= difficulty) return String(i + k);
    }
    i += 64;
    await new Promise((r) => setTimeout(r, 0));
  }
}

/* Invisible proof-of-work: solves in background on mount, refreshes silently. */
export function useSecurity() {
  const mountedAt = useRef(Date.now());
  const proofRef = useRef(null);
  const pending = useRef(null);
  const [hp, setHp] = useState("");
  const [ready, setReady] = useState(false);

  const prepare = useCallback(() => {
    if (pending.current) return pending.current;
    pending.current = axios.get(`${API}/security/challenge`)
      .then(async ({ data }) => {
        const solution = await solve(data.nonce, data.difficulty_bits);
        proofRef.current = { challenge_id: data.challenge_id, solution, expires: Date.now() + (data.ttl - 30) * 1000 };
        setReady(true);
        return proofRef.current;
      })
      .finally(() => { pending.current = null; });
    return pending.current;
  }, []);

  useEffect(() => { prepare().catch(() => {}); }, [prepare]);

  const getProof = useCallback(async () => {
    if (!proofRef.current || proofRef.current.expires < Date.now()) await prepare();
    const p = proofRef.current;
    proofRef.current = null;
    setReady(false);
    prepare().catch(() => {});
    return { challenge_id: p.challenge_id, solution: p.solution, hp, elapsed_ms: Date.now() - mountedAt.current };
  }, [prepare, hp]);

  const reset = useCallback(() => { proofRef.current = null; setReady(false); prepare().catch(() => {}); }, [prepare]);

  const Honeypot = () => (
    <input type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)}
      className="hp-field" aria-hidden="true" data-testid="honeypot-field" />
  );

  return { getProof, reset, ready, Honeypot };
}
